import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { SITE_CONTENT as content } from './content'
import './index.less'

const MOBILE_MQ = '(max-width: 767px)'
const DISCLOSURE_DURATION = 460
const RAISE_MARKERS = [
  'RAISE（Research on AI and Smart Employment）',
  'RAISE (Research on AI and Smart Employment)',
]

/** Rspack 下 SVG 经 SVGR 为组件，位图为 URL 字符串 */
function AssetImage({ src, alt = '', className, width, height, ...rest }) {
  if (src == null) return null
  if (typeof src === 'string') {
    return (
      <img
        className={className}
        src={src}
        alt={alt}
        width={width}
        height={height}
        {...rest}
      />
    )
  }
  const Svg = src.default || src
  return (
    <Svg
      className={className}
      width={width}
      height={height}
      role={alt ? 'img' : 'presentation'}
      aria-label={alt || undefined}
      aria-hidden={alt ? undefined : true}
      {...rest}
    />
  )
}

function prefersReducedMotion() {
  return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

function Lines({ text }) {
  if (text == null) return null
  const parts = String(text).split('\n')
  return parts.map((line, index) => (
    <React.Fragment key={index}>
      {index > 0 ? <br /> : null}
      {line}
    </React.Fragment>
  ))
}

function TimelineLines({ text }) {
  return String(text == null ? '' : text)
    .split('\n')
    .map((line, index) => (
      <span
        key={index}
        className={`timeline-item__line${line === '|' ? ' timeline-item__separator' : ''}`}
      >
        {line}
      </span>
    ))
}

function emphasizeRaise(text) {
  const nodes = []
  let remaining = text
  let key = 0
  while (remaining.length) {
    let hitIndex = -1
    let hitMarker = null
    RAISE_MARKERS.forEach((marker) => {
      const index = remaining.indexOf(marker)
      if (index !== -1 && (hitIndex === -1 || index < hitIndex)) {
        hitIndex = index
        hitMarker = marker
      }
    })
    if (hitIndex === -1) {
      nodes.push(remaining)
      break
    }
    if (hitIndex > 0) nodes.push(remaining.slice(0, hitIndex))
    nodes.push(<strong key={`raise-${key++}`}>{hitMarker}</strong>)
    remaining = remaining.slice(hitIndex + hitMarker.length)
  }
  return nodes
}

function clearDisclosure(element, hidden) {
  if (!element) return
  if (element.__disclosureAnimation) element.__disclosureAnimation.cancel()
  element.classList.remove('is-disclosure-open', 'is-disclosure-closed')
  element.style.removeProperty('max-height')
  element.style.removeProperty('opacity')
  element.style.removeProperty('transform')
  element.style.removeProperty('margin-top')
  element.hidden = hidden
}

function animateDisclosure(element, open, onComplete) {
  if (!element) {
    if (onComplete) onComplete()
    return
  }
  if (element.__disclosureAnimation) return

  let finished = false
  let frameId = 0
  let timerId = 0
  const onTransitionEnd = (event) => {
    if (event.target === element && event.propertyName === 'max-height') finish()
  }
  const cancel = () => {
    if (finished) return
    finished = true
    if (frameId) window.cancelAnimationFrame(frameId)
    window.clearTimeout(timerId)
    element.removeEventListener('transitionend', onTransitionEnd)
    element.classList.remove('is-disclosure-open', 'is-disclosure-closed')
    element.style.removeProperty('max-height')
    element.style.removeProperty('opacity')
    element.style.removeProperty('transform')
    element.style.removeProperty('margin-top')
    delete element.__disclosureAnimation
  }
  const finish = () => {
    if (finished) return
    finished = true
    if (frameId) window.cancelAnimationFrame(frameId)
    window.clearTimeout(timerId)
    element.removeEventListener('transitionend', onTransitionEnd)
    element.classList.remove('is-disclosure-open', 'is-disclosure-closed')
    element.style.removeProperty('max-height')
    element.style.removeProperty('opacity')
    element.style.removeProperty('transform')
    element.style.removeProperty('margin-top')
    element.hidden = !open
    delete element.__disclosureAnimation
    if (onComplete) onComplete()
  }

  element.__disclosureAnimation = { cancel }
  element.addEventListener('transitionend', onTransitionEnd)
  element.hidden = false

  if (prefersReducedMotion()) {
    finish()
    return
  }

  if (open) {
    element.classList.add('is-disclosure-closed')
    element.style.maxHeight = '0px'
    void element.offsetHeight
    frameId = window.requestAnimationFrame(() => {
      if (finished) return
      element.classList.remove('is-disclosure-closed')
      element.classList.add('is-disclosure-open')
      element.style.maxHeight = `${element.scrollHeight}px`
    })
  } else {
    element.classList.add('is-disclosure-open')
    element.style.maxHeight = 'none'
    const fullHeight = element.scrollHeight
    element.style.maxHeight = `${fullHeight}px`
    void element.offsetHeight
    frameId = window.requestAnimationFrame(() => {
      if (finished) return
      element.classList.remove('is-disclosure-open')
      element.classList.add('is-disclosure-closed')
      element.style.maxHeight = '0px'
    })
  }
  timerId = window.setTimeout(finish, DISCLOSURE_DURATION + 100)
}

function setInert(element, inert) {
  if (!element) return
  if (inert) element.setAttribute('inert', '')
  else element.removeAttribute('inert')
  element.inert = inert
}

function BrandLogo({ className }) {
  return (
    <AssetImage
      className={className || 'brand-logo'}
      src={content.brand.logo}
      alt={content.brand.name}
      width="589"
      height="224"
    />
  )
}

function SectionHeading({ id, data }) {
  return (
    <h2 id={id} className="section-heading">
      <span>{data.eyebrow}</span>
      <small>{data.english}</small>
    </h2>
  )
}

function isWeChatUA() {
  if (typeof navigator === 'undefined') return false
  return /MicroMessenger/i.test(navigator.userAgent)
}

/** 尝试自动播放；微信等环境失败时展示播放按钮 */
function HeroVideo({
  variant,
  className,
  src,
  poster,
  label,
  preload = 'metadata',
}) {
  const videoRef = useRef(null)
  const [needsGesture, setNeedsGesture] = useState(() => isWeChatUA())
  const [playing, setPlaying] = useState(false)

  useEffect(() => {
    const video = videoRef.current
    if (!video) return undefined

    video.setAttribute('webkit-playsinline', 'true')
    video.setAttribute('playsinline', 'true')
    video.setAttribute('x5-playsinline', 'true')
    video.setAttribute('x5-video-player-type', 'h5')
    video.setAttribute('x5-video-player-fullscreen', 'false')

    const markPlaying = () => {
      setPlaying(true)
      setNeedsGesture(false)
    }
    const markPaused = () => {
      setPlaying(false)
      if (!video.ended) setNeedsGesture(true)
    }

    const tryPlay = () => {
      const result = video.play()
      if (result && typeof result.then === 'function') {
        result.then(markPlaying).catch(() => {
          setPlaying(false)
          setNeedsGesture(true)
        })
      } else if (!video.paused) {
        markPlaying()
      }
    }

    video.addEventListener('playing', markPlaying)
    video.addEventListener('play', markPlaying)
    video.addEventListener('pause', markPaused)
    tryPlay()
    const timer = window.setTimeout(() => {
      if (video.paused) {
        setPlaying(false)
        setNeedsGesture(true)
      }
    }, 600)

    return () => {
      window.clearTimeout(timer)
      video.removeEventListener('playing', markPlaying)
      video.removeEventListener('play', markPlaying)
      video.removeEventListener('pause', markPaused)
    }
  }, [src])

  const handlePlayClick = (event) => {
    event.preventDefault()
    event.stopPropagation()
    const video = videoRef.current
    if (!video) return
    const result = video.play()
    if (result && typeof result.then === 'function') {
      result
        .then(() => {
          setPlaying(true)
          setNeedsGesture(false)
        })
        .catch(() => {
          setNeedsGesture(true)
        })
    }
  }

  const showWeChatPoster = Boolean(poster) && isWeChatUA()

  return (
    <div
      className={`hero-video hero-video--${variant}${needsGesture ? ' is-waiting' : ''}${
        playing ? ' is-playing' : ''
      }`}
    >
      {showWeChatPoster ? (
        <AssetImage
          className="hero-video__poster"
          src={poster}
          alt=""
          aria-hidden="true"
        />
      ) : null}
      <video
        ref={videoRef}
        className={className}
        muted
        loop
        autoPlay
        playsInline
        preload={preload}
        poster={showWeChatPoster && typeof poster === 'string' ? poster : undefined}
        aria-label={label}
      >
        <source src={src} type="video/mp4" />
      </video>
      {needsGesture ? (
        <button
          type="button"
          className="hero-video__play"
          onClick={handlePlayClick}
          aria-label="播放视频"
        >
          <span className="hero-video__play-icon" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  )
}

/** 项目介绍视频不自动播放，各端展示封面和播放按钮 */
function ProgramMediaVideo({ src, poster, label }) {
  const videoRef = useRef(null)
  const [playing, setPlaying] = useState(false)
  const showPoster = Boolean(poster)

  useEffect(() => {
    const video = videoRef.current
    if (!video) return undefined
    video.setAttribute('webkit-playsinline', 'true')
    video.setAttribute('playsinline', 'true')
    video.setAttribute('x5-playsinline', 'true')
    video.setAttribute('x5-video-player-type', 'h5')
    video.setAttribute('x5-video-player-fullscreen', 'false')
    return undefined
  }, [src])

  const handlePlayClick = (event) => {
    event.preventDefault()
    event.stopPropagation()
    const video = videoRef.current
    if (!video) return
    const result = video.play()
    if (result && typeof result.then === 'function') {
      result
        .then(() => setPlaying(true))
        .catch(() => setPlaying(false))
    }
  }

  return (
    <div className={`program-media__player${playing ? ' is-playing' : ''}`}>
      {showPoster ? (
        <AssetImage
          className="program-media__poster"
          src={poster}
          alt=""
          aria-hidden="true"
        />
      ) : null}
      <video
        ref={videoRef}
        className="program-media__asset"
        controls={playing}
        playsInline
        preload="metadata"
        poster={showPoster && typeof poster === 'string' ? poster : undefined}
        aria-label={label}
        onPlay={() => setPlaying(true)}
        onPause={(event) => {
          if (!event.currentTarget.ended) setPlaying(false)
        }}
      >
        <source src={src} type="video/mp4" />
        <p>您的浏览器暂不支持视频播放。</p>
      </video>
      {playing ? null : (
        <button
          type="button"
          className="hero-video__play program-media__play"
          onClick={handlePlayClick}
          aria-label="播放视频"
        >
          <span className="hero-video__play-icon" aria-hidden="true" />
        </button>
      )}
    </div>
  )
}

function App() {
  const [isMobile, setIsMobile] = useState(
    () => (typeof window !== 'undefined' ? window.matchMedia(MOBILE_MQ).matches : false)
  )
  const [menuOpen, setMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [aboutExpanded, setAboutExpanded] = useState(false)
  const [expandedPropositions, setExpandedPropositions] = useState(() => new Set())
  const aboutAnimatingRef = useRef(false)
  const propositionAnimatingRef = useRef({})
  const aboutExtendedRef = useRef(null)
  const aboutToggleRef = useRef(null)
  const menuRef = useRef(null)
  const menuToggleRef = useRef(null)
  const mainRef = useRef(null)
  const footerRef = useRef(null)
  const skipLinkRef = useRef(null)
  const floatingRef = useRef(null)
  const propositionBodyRefs = useRef({})

  const signupHref = content.signupUrl || '#signup'
  const pageExpanded = isMobile && aboutExpanded

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_MQ)
    const onChange = (event) => {
      aboutAnimatingRef.current = false
      propositionAnimatingRef.current = {}
      setMenuOpen(false)
      setIsMobile(event.matches)
    }
    if (mq.addEventListener) mq.addEventListener('change', onChange)
    else mq.addListener(onChange)
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange)
      else mq.removeListener(onChange)
    }
  }, [])

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 100)
    window.addEventListener('scroll', onScroll, { passive: true })
    onScroll()
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    document.body.classList.toggle('menu-open', menuOpen)
    setInert(menuRef.current, !menuOpen)
    setInert(mainRef.current, menuOpen)
    setInert(footerRef.current, menuOpen)
    setInert(skipLinkRef.current, menuOpen)
    setInert(floatingRef.current, menuOpen)
  }, [menuOpen])

  // 断点切换时同步 disclosure，不与点击动画抢状态
  useLayoutEffect(() => {
    const extended = aboutExtendedRef.current
    if (!isMobile) {
      clearDisclosure(extended, false)
      Object.keys(propositionBodyRefs.current).forEach((key) => {
        clearDisclosure(propositionBodyRefs.current[key], false)
      })
      return
    }
    if (!aboutAnimatingRef.current) {
      clearDisclosure(extended, !aboutExpanded)
    }
    content.propositions.items.forEach((item) => {
      if (propositionAnimatingRef.current[item.number]) return
      clearDisclosure(
        propositionBodyRefs.current[item.number],
        !expandedPropositions.has(item.number)
      )
    })
  }, [isMobile])

  const closeMenu = useCallback((restoreFocus = false) => {
    setMenuOpen(false)
    if (restoreFocus && menuToggleRef.current) {
      menuToggleRef.current.focus({ preventScroll: true })
    }
  }, [])

  useEffect(() => {
    if (!menuOpen) return undefined
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeMenu(true)
        return
      }
      if (event.key !== 'Tab' || !menuRef.current || !menuToggleRef.current) return
      const links = [...menuRef.current.querySelectorAll('a')]
      const first = menuToggleRef.current
      const last = links[links.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [menuOpen, closeMenu])

  const handleAboutToggle = () => {
    if (!isMobile || aboutAnimatingRef.current || !aboutToggleRef.current) return
    const oldTop = aboutToggleRef.current.getBoundingClientRect().top
    const next = !aboutExpanded
    aboutAnimatingRef.current = true
    setAboutExpanded(next)
    animateDisclosure(aboutExtendedRef.current, next, () => {
      aboutAnimatingRef.current = false
      if (!next && aboutToggleRef.current) {
        const delta = aboutToggleRef.current.getBoundingClientRect().top - oldTop
        if (Math.abs(delta) > 1) window.scrollBy({ top: delta, behavior: 'instant' })
      }
    })
  }

  const handlePropositionToggle = (number) => {
    if (propositionAnimatingRef.current[number]) return
    const open = !expandedPropositions.has(number)
    const body = propositionBodyRefs.current[number]
    propositionAnimatingRef.current[number] = true
    setExpandedPropositions((prev) => {
      const next = new Set(prev)
      if (open) next.add(number)
      else next.delete(number)
      return next
    })
    animateDisclosure(body, open, () => {
      propositionAnimatingRef.current[number] = false
    })
  }

  const handleMenuLinkClick = (event) => {
    const link = event.target.closest('a')
    if (event.target === menuRef.current || link) {
      closeMenu(true)
      if (link && link.hash) {
        const target = document.getElementById(link.hash.slice(1))
        if (target) {
          target.setAttribute('tabindex', '-1')
          target.focus({ preventScroll: true })
        }
      }
    }
  }

  const leadParagraphs = content.about.lead.map((paragraph, index) =>
    isMobile && index === 1 ? content.about.mobileSecondParagraph : paragraph
  )

  const formatTimelineDate = (item) => {
    const raw = isMobile && item.mobileDate ? item.mobileDate : item.date
    return raw.replace(' - ', '\n|\n').replace(' 或 ', '\n或\n')
  }

  return (
    <div className={`page-root${pageExpanded ? ' is-about-expanded' : ''}`}>
      <div className="page-root__glow" aria-hidden="true">
        <AssetImage src={content.assets.circleGradient} alt="" />
      </div>
      <a className="skip-link" href="#about" ref={skipLinkRef}>
        跳至主要内容
      </a>

      <header
        className={`site-header${scrolled ? ' is-scrolled' : ''}${menuOpen ? ' is-menu-open' : ''}`}
        data-header
      >
        <div className="container site-header__inner">
          <a className="site-header__brand" href="#top" aria-label="猎聘首页" onClick={() => closeMenu()}>
            <BrandLogo />
          </a>
          <a className="button site-header__cta" href={signupHref}>
            立即报名
          </a>
          <button
            className="menu-toggle"
            type="button"
            aria-expanded={menuOpen}
            aria-controls="mobile-menu"
            aria-label={menuOpen ? '关闭导航菜单' : '打开导航菜单'}
            ref={menuToggleRef}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </header>

      <nav
        className={`mobile-menu${menuOpen ? ' is-open' : ''}`}
        id="mobile-menu"
        aria-label="页面导航"
        ref={menuRef}
        onClick={handleMenuLinkClick}
      >
        <div className="mobile-menu__panel">
          {content.navigation.map((item) => (
            <a
              key={item.label}
              className={`mobile-menu__link${item.isCta ? ' mobile-menu__link--cta' : ''}`}
              href={item.isCta ? signupHref : item.href}
            >
              {item.label}
            </a>
          ))}
        </div>
      </nav>

      <main ref={mainRef}>
        <section
          className={`hero${content.brand.heroMobileVideo ? ' hero--mobile-video' : ''}${
            pageExpanded ? ' is-about-expanded' : ''
          }`}
          id="top"
          aria-labelledby="hero-title"
        >
          <h1 className="sr-only" id="hero-title">
            {content.brand.title} {content.brand.year}
          </h1>
          {isMobile ? (
            <div className="hero__desktop-art" aria-hidden="true" />
          ) : content.brand.heroDesktopVideo ? (
            <HeroVideo
              variant="desktop"
              className="hero__desktop-art hero__desktop-video"
              src={content.brand.heroDesktopVideo}
              poster={content.brand.heroDesktopPoster}
              label="非凡 2026 主视觉视频"
              preload="metadata"
            />
          ) : (
            <AssetImage
              className="hero__desktop-art"
              src={content.brand.heroDesktopPoster || content.brand.heroDesktop}
              alt=""
              width="2560"
              height="1440"
              decoding="async"
            />
          )}
          <div className="hero__mobile-backdrop" aria-hidden="true" />
          {content.brand.heroMobileVideo && isMobile ? (
            <HeroVideo
              variant="mobile"
              className="hero__mobile-full-video"
              src={content.brand.heroMobileVideo}
              poster={content.brand.heroMobilePoster}
              label="非凡 2026 H5 主视觉视频"
              preload="auto"
            />
          ) : (
            <div className="hero__mobile-mark" aria-hidden="true" />
          )}
        </section>

        <section
          className={`about section${pageExpanded ? ' is-expanded' : ''}`}
          id="about"
          aria-labelledby="about-title"
        >
          <div className="about__decoration" aria-hidden="true">
            <AssetImage
              src={content.assets.circleGradient}
              alt=""
              width="1600"
              height="1600"
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="container about__grid">
            <SectionHeading id="about-title" data={content.about} />
            <div className="about__lead">
              {leadParagraphs.map((paragraph, index) => (
                <p key={index}>{emphasizeRaise(paragraph)}</p>
              ))}
            </div>
            <div
              className="about__extended"
              id="about-extended"
              ref={(node) => {
                aboutExtendedRef.current = node
                if (node && isMobile && !aboutExpanded && !aboutAnimatingRef.current) {
                  node.hidden = true
                }
              }}
            >
              {content.about.extended.map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>
            <figure
              className={`program-media${content.media.type === 'video' ? ' program-media--video' : ''}`}
            >
              <div className="program-media__visual">
                {content.media.type === 'video' ? (
                  <ProgramMediaVideo
                    src={
                      isMobile
                        ? content.media.mobileSrc || content.media.desktopSrc
                        : content.media.desktopSrc || content.media.mobileSrc
                    }
                    poster={content.media.poster}
                    label="项目介绍视频"
                  />
                ) : (
                  <picture>
                    <source media="(max-width: 767px)" srcSet={content.media.mobileSrc || content.media.desktopSrc} />
                    <img
                      className="program-media__asset"
                      src={content.media.desktopSrc}
                      alt={content.media.alt}
                      width="506"
                      height="285"
                    />
                  </picture>
                )}
              </div>
              <a className="button program-media__cta" href={signupHref}>
                立即报名
              </a>
            </figure>
            <button
              className="about__toggle"
              type="button"
              ref={aboutToggleRef}
              aria-expanded={pageExpanded}
              aria-controls="about-extended"
              onClick={handleAboutToggle}
            >
              <AssetImage src={content.assets.aboutToggle} alt="" width="70" height="70" />
              <span>{pageExpanded ? '收起' : '查看更多'}</span>
            </button>
          </div>
        </section>

        <section className="propositions section" id="propositions" aria-labelledby="propositions-title">
          <div className="container propositions__grid">
            <h2 id="propositions-title" className="propositions__heading">
              <Lines
                text={isMobile ? content.propositions.heading : content.propositions.desktopHeading}
              />
            </h2>
            <div className="propositions__list">
              {content.propositions.items.map((item) => {
                const expanded = expandedPropositions.has(item.number)
                return (
                  <article
                    key={item.number}
                    className={`proposition proposition--${item.color}${expanded ? ' is-expanded' : ''}`}
                  >
                    <span className="proposition__number">{item.number}</span>
                    <div className="proposition__copy">
                      <h3>
                        {isMobile ? (
                          <button
                            className="proposition__toggle"
                            type="button"
                            aria-expanded={expanded}
                            aria-controls={`proposition-body-${item.number}`}
                            onClick={() => handlePropositionToggle(item.number)}
                          >
                            <span className="proposition__toggle-label">{item.title}</span>
                            <AssetImage
                              src={content.assets.propositionToggles[item.color]}
                              alt=""
                              width="40"
                              height="40"
                            />
                          </button>
                        ) : (
                          item.title
                        )}
                      </h3>
                      <p
                        id={`proposition-body-${item.number}`}
                        ref={(node) => {
                          propositionBodyRefs.current[item.number] = node
                          if (node && isMobile && !expanded && !propositionAnimatingRef.current[item.number]) {
                            node.hidden = true
                          }
                        }}
                      >
                        {item.body}
                      </p>
                    </div>
                  </article>
                )
              })}
            </div>
          </div>
        </section>

        <section className="timeline section" id="timeline" aria-labelledby="timeline-title">
          <div className="container timeline__panel">
            <SectionHeading id="timeline-title" data={content.timeline} />
            <div className="timeline__track">
              {content.timeline.items.map((item, index) => {
                const title = isMobile && item.mobileTitle ? item.mobileTitle : item.title
                const axisClass = index % 2 === 1 ? ' timeline-item--lower' : ' timeline-item--upper'
                return (
                  <article key={item.title} className={`timeline-item${axisClass}`}>
                    <div className="timeline-item__date">
                      <TimelineLines text={formatTimelineDate(item)} />
                    </div>
                    <AssetImage
                      className="timeline-item__dot"
                      src={content.assets.timelineDot}
                      alt=""
                      width="12"
                      height="12"
                    />
                    <div
                      className={`timeline-item__content${
                        title.includes('\n') ? ' timeline-item__content--multiline' : ''
                      }`}
                    >
                      <h3>
                        <TimelineLines text={title} />
                      </h3>
                      <p>
                        <Lines text={item.description} />
                      </p>
                    </div>
                  </article>
                )
              })}
            </div>
          </div>
        </section>

        <section className="signup section" id="signup" aria-labelledby="signup-title">
          <div className="container signup__inner">
            <div className="signup__copy">
              <h2 id="signup-title">{content.signup.title}</h2>
              <p>
                <Lines text={content.signup.description} />
              </p>
            </div>
            <div className="signup__qr">
              {content.signup.qrSrc ? (
                <AssetImage
                  src={content.signup.qrSrc}
                  alt="非凡2026项目咨询二维码"
                  width="132"
                  height="132"
                />
              ) : (
                <div className="qr-placeholder" role="img" aria-label={content.signup.qrLabel} />
              )}
            </div>
          </div>
        </section>
      </main>

      <a className="floating-signup" href={signupHref} ref={floatingRef}>
        <span>立即报名</span>
      </a>

      <footer className="site-footer" ref={footerRef}>
        <div className="site-footer__glow" aria-hidden="true">
          <AssetImage
            src={content.assets.circleGradient}
            alt=""
            width="1600"
            height="1600"
            loading="lazy"
            decoding="async"
          />
        </div>
        <div className="container site-footer__grid">
          <div className="site-footer__brand">
            <a className="site-footer__logo" href="#top" aria-label="返回顶部">
              <BrandLogo />
            </a>
            <p>{content.brand.slogan}</p>
          </div>
          <div className="site-footer__contact">
            <h2>
              {content.contact.heading}
              <small>{content.contact.english}</small>
            </h2>
            <div className="site-footer__emails">
              <p>
                <Lines text={content.contact.media} />
              </p>
              <p>
                <Lines text={content.contact.consultation} />
              </p>
            </div>
          </div>
          <p className="site-footer__legal">
            <Lines text={content.contact.legal} />
          </p>
        </div>
        <AssetImage
          className="site-footer__mobile-slogan"
          src={content.brand.sloganImage}
          alt={content.brand.slogan}
          width="467"
          height="30"
        />
      </footer>
    </div>
  )
}

export default App
